import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyForm = { stableName: '' };

const StableList = () => {
  const { t, i18n } = useTranslation();
  const [stables, setStables] = useState([]);
  const [deleteStableId, setDeleteStableId] = useState(null);
  const [editStableId, setEditStableId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const userId = localStorage.getItem('userId');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchStables = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/stables`);
      setStables(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('stable_list_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStables();
  }, [fetchStables]);

  const filteredStables = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return stables;
    return stables.filter((stable) => String(stable.stable_name || '').toLowerCase().includes(term));
  }, [search, stables]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedStables,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredStables, [search]);

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setEditStableId(null);
    setIsAddDialogOpen(false);
  }, []);

  const openAddDialog = useCallback(() => {
    setMessage('');
    setError('');
    setForm(emptyForm);
    setIsAddDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((stable) => {
    setMessage('');
    setError('');
    setEditStableId(stable.stable_id);
    setForm({ stableName: stable.stable_name || '' });
  }, []);

  const validateForm = useCallback(() => {
    if (!form.stableName.trim()) {
      setError(t('stable_name_required'));
      return false;
    }
    return true;
  }, [form.stableName, t]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/stables/${deleteStableId}`, {
        data: { deleted_by: userId },
      });
      setDeleteStableId(null);
      setMessage(t('stable_deleted_successfully'));
      await fetchStables();
    } catch (err) {
      setError(err?.response?.data?.error || t('stable_delete_error'));
    } finally {
      setSaving(false);
    }
  }, [deleteStableId, fetchStables, t, userId]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (editStableId) {
        await api.put(`${apiUrl}/stables/${editStableId}`, {
          stable_name: form.stableName.trim(),
          modified_by: userId,
        });
        setMessage(t('stable_updated_successfully'));
      } else {
        await api.post(`${apiUrl}/stables`, {
          stable_name: form.stableName.trim(),
          created_by: userId,
        });
        setMessage(t('stable_added_successfully'));
      }
      resetForm();
      await fetchStables();
    } catch (err) {
      setError(err?.response?.data?.error || (editStableId ? t('stable_update_error') : t('error_adding_stable')));
    } finally {
      setSaving(false);
    }
  }, [editStableId, fetchStables, form.stableName, resetForm, t, userId, validateForm]);

  return (
    <Box
      className="smart-page-container"
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('stable_list')}
        subtitle={t('stable_list_hint')}
        count={stables.length}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('add_stable')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_stables')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 360 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('stable_name')}</TableCell>
                <TableCell align="center" sx={{ width: 150 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStables.map((stable, index) => (
                <TableRow key={stable.stable_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 220 }}>{stable.stable_name}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit_stable')}>
                        <IconButton onClick={() => openEditDialog(stable)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete_stable')}>
                        <IconButton onClick={() => setDeleteStableId(stable.stable_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredStables.length && !loading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_stables_found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredStables.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={Boolean(deleteStableId)} onClose={() => setDeleteStableId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('delete_confirmation_message')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteStableId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editStableId) || isAddDialogOpen} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{editStableId ? t('edit_stable') : t('add_stable')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('stable_name')}
            value={form.stableName}
            onChange={(event) => setForm({ stableName: event.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StableList;
